/**
 * Shift Matcher - Pure Functions für regelbasierte Schicht-Ermittlung
 *
 * ARCHITEKTUR:
 * - Pure Functions ohne Side Effects
 * - Keine DB-Abhängigkeit (Regeln werden übergeben)
 * - Europe/Berlin ist verpflichtend
 * - DST-korrekt durch bestehende date-range Utilities
 *
 * ALGORITHMUS:
 * 1. Berlin-Zeit extrahieren (Wochentag ISO 1-7, Tagesminute 0-1439)
 * 2. Filter: weekday in rule.weekdays
 * 3. Filter: validFrom/validUntil
 * 4. Filter: time-in-range (mit Overnight-Support)
 * 5. Sort: priority DESC, code ASC (deterministisch)
 * 6. Return erste passende Regel
 */
import { formatBerlinDate, getBerlinDayRange } from './date-range';
const BERLIN_TZ = 'Europe/Berlin';
// =============================================================================
// BERLIN TIME HELPERS
// =============================================================================
/**
 * Extrahiert Berlin-Zeit Komponenten aus einem UTC-Timestamp.
 * DST-korrekt durch Intl.DateTimeFormat.
 */
export function toBerlinTime(timestamp) {
    // Intl.DateTimeFormat ist DST-korrekt
    const formatter = new Intl.DateTimeFormat('en-GB', {
        timeZone: BERLIN_TZ,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    });
    const parts = formatter.formatToParts(timestamp);
    const getPart = (type) => parts.find(p => p.type === type)?.value || '';
    const year = parseInt(getPart('year'), 10);
    const month = parseInt(getPart('month'), 10);
    const day = parseInt(getPart('day'), 10);
    const hour = parseInt(getPart('hour'), 10);
    const minute = parseInt(getPart('minute'), 10);
    // Wochentag: JS getDay() = 0=So, wir brauchen ISO = 1=Mo, 7=So
    const weekdayShort = getPart('weekday');
    const weekdayMap = {
        'Mon': 1, 'Tue': 2, 'Wed': 3, 'Thu': 4, 'Fri': 5, 'Sat': 6, 'Sun': 7,
    };
    const weekday = weekdayMap[weekdayShort] || 1;
    return {
        year,
        month,
        day,
        weekday,
        hour,
        minute,
        minuteOfDay: hour * 60 + minute,
    };
}
/**
 * Parst "HH:MM" zu Tagesminuten (0-1439)
 */
export function parseTimeToMinutes(time) {
    const [h, m] = time.split(':').map(Number);
    return h * 60 + m;
}
// =============================================================================
// MATCHING LOGIC
// =============================================================================
/**
 * Prüft, ob eine Regel zum Zeitpunkt gültig ist (validFrom/validUntil).
 * null bedeutet "unbegrenzt".
 */
function isValidAtTimestamp(rule, timestamp) {
    if (rule.validFrom && timestamp < rule.validFrom)
        return false;
    if (rule.validUntil && timestamp > rule.validUntil)
        return false;
    return true;
}
/**
 * Prüft, ob minuteOfDay im Zeitfenster liegt.
 *
 * Overnight-Handling:
 * - Normal (end > start): start <= minute < end
 * - Overnight (end < start): minute >= start OR minute < end
 * - Gleich (end === start): 24h Schicht (immer true)
 */
function isTimeInRange(minuteOfDay, startTime, endTime) {
    const startMin = parseTimeToMinutes(startTime);
    const endMin = parseTimeToMinutes(endTime);
    if (endMin > startMin) {
        // Normal: z.B. 04:00-14:45
        return minuteOfDay >= startMin && minuteOfDay < endMin;
    }
    else if (endMin < startMin) {
        // Overnight: z.B. 14:45-04:00
        return minuteOfDay >= startMin || minuteOfDay < endMin;
    }
    else {
        // start === end: 24h Schicht (unwahrscheinlich, aber definiert)
        return true;
    }
}
/**
 * Findet die passende Schichtregel für einen Timestamp.
 *
 * @param rules - Aktive Schichtregeln (vom Backend geladen)
 * @param timestamp - Zeitpunkt für die Schichtermittlung
 * @returns Passende Regel + shiftDate, oder null wenn keine Regel passt
 *
 * OVERNIGHT-HANDLING:
 * Bei Overnight-Regeln (endTime < startTime) und minuteOfDay < endMin
 * befinden wir uns im "nächsten Tag"-Teil der Schicht. Der Wochentag
 * für das Matching muss dann der VORTAG sein (Schicht begann gestern).
 */
export function matchShiftRule(rules, timestamp) {
    const berlin = toBerlinTime(timestamp);
    const candidates = rules
        // 1. Wochentag + Zeitfenster kombiniert prüfen (wegen Overnight)
        .filter(r => matchesWeekdayAndTime(r, berlin))
        // 2. Gültigkeit muss passen
        .filter(r => isValidAtTimestamp(r, timestamp))
        // 3. Sortieren: priority DESC, dann code ASC (deterministisch)
        .sort((a, b) => {
        if (b.priority !== a.priority)
            return b.priority - a.priority;
        return a.code.localeCompare(b.code);
    });
    if (candidates.length === 0)
        return null;
    const rule = candidates[0];
    const shiftDate = calculateShiftDate(rule, timestamp, berlin);
    const shiftDateStr = formatBerlinDate(shiftDate);
    return {
        rule,
        shiftDate,
        shiftDateStr,
        weekday: berlin.weekday,
    };
}
/**
 * Prüft ob eine Regel für den Timestamp matcht (Wochentag + Zeitfenster).
 *
 * Bei Overnight-Regeln gibt es zwei Fälle:
 * 1. Wir sind im "Start-Tag"-Teil (minuteOfDay >= startMin):
 *    → Wochentag muss in rule.weekdays sein
 * 2. Wir sind im "nächsten Tag"-Teil (minuteOfDay < endMin):
 *    → VORTAG muss in rule.weekdays sein (Schicht begann gestern)
 */
function matchesWeekdayAndTime(rule, berlin) {
    const startMin = parseTimeToMinutes(rule.startTime);
    const endMin = parseTimeToMinutes(rule.endTime);
    const isOvernight = endMin < startMin;
    // start==end ist ungültig und sollte nicht vorkommen (Backend-Validierung)
    // Falls doch: behandeln als "nie aktiv" (sicherer Default)
    if (startMin === endMin) {
        return false;
    }
    if (!isOvernight) {
        // Normal: z.B. 04:00-14:45
        // Zeitfenster: start <= minute < end
        if (berlin.minuteOfDay < startMin || berlin.minuteOfDay >= endMin) {
            return false;
        }
        return rule.weekdays.includes(berlin.weekday);
    }
    // Overnight: z.B. 23:00-04:00 oder 14:45-04:00
    // Zeitfenster: minute >= start ODER minute < end
    const inStartPart = berlin.minuteOfDay >= startMin;
    const inEndPart = berlin.minuteOfDay < endMin;
    if (!inStartPart && !inEndPart) {
        // Weder im Start- noch im End-Teil → nicht in dieser Schicht
        return false;
    }
    if (inStartPart) {
        // Im Start-Tag-Teil (z.B. Mo 23:30 bei 23:00-04:00)
        // → Aktueller Wochentag muss passen
        return rule.weekdays.includes(berlin.weekday);
    }
    // Im nächsten-Tag-Teil (z.B. Di 02:00 bei Mo-Regel 23:00-04:00)
    // → VORTAG muss in weekdays sein
    const yesterdayWeekday = berlin.weekday === 1 ? 7 : berlin.weekday - 1;
    return rule.weekdays.includes(yesterdayWeekday);
}
/**
 * Berechnet das Schichtdatum (Tag des Schichtbeginns in Europe/Berlin).
 *
 * Definition:
 * - shiftDate ist IMMER der Tag, an dem die Schicht BEGINNT
 * - Bei Overnight (z.B. 14:45-04:00):
 *   - 23:00 Di → shiftDate = Di (Schicht begann Di 14:45)
 *   - 02:00 Mi → shiftDate = Di (wir sind noch in der Di-Spätschicht)
 *
 * Algorithmus:
 * - Wenn Overnight UND minuteOfDay < endMin: shiftDate = Berlin-Vortag
 * - Sonst: shiftDate = Berlin-Heute
 */
function calculateShiftDate(rule, timestamp, berlin) {
    const startMin = parseTimeToMinutes(rule.startTime);
    const endMin = parseTimeToMinutes(rule.endTime);
    // Overnight erkennen: end < start (z.B. 14:45-04:00)
    const isOvernight = endMin < startMin;
    // Wir sind im "nächsten Tag"-Teil wenn overnight und vor endTime
    const isInNextDayPart = isOvernight && berlin.minuteOfDay < endMin;
    if (isInNextDayPart) {
        // Vortag in Berlin. NICHT ueber "minus 24 Stunden" — in der Nacht der
        // Fruehjahrsumstellung hat der Berliner Tag nur 23 Stunden, und der
        // Sprung landete zwei Tage zurueck statt einem (Regressionstest:
        // shift-matcher.nachtschicht.test.ts). Stattdessen 1 ms vor den Beginn
        // des aktuellen Berliner Tages gehen — das ist per Definition der
        // Vortag, unabhaengig von seiner Laenge.
        const heuteBeginn = getBerlinDayRange(timestamp).start;
        return getBerlinDayRange(new Date(heuteBeginn.getTime() - 1)).start;
    }
    // Aktueller Berlin-Tag
    return getBerlinDayRange(timestamp).start;
}
/**
 * Berechnet das Schichtdatum für eine Regel und einen Timestamp.
 * Wrapper für externe Nutzung.
 */
export function getShiftDate(rule, timestamp) {
    const berlin = toBerlinTime(timestamp);
    return calculateShiftDate(rule, timestamp, berlin);
}
// =============================================================================
// DISPLAY HELPERS
// =============================================================================
/**
 * Formatiert einen ISO-Wochentag als deutschen Text
 */
export function formatWeekday(weekday) {
    const names = {
        1: 'Montag',
        2: 'Dienstag',
        3: 'Mittwoch',
        4: 'Donnerstag',
        5: 'Freitag',
        6: 'Samstag',
        7: 'Sonntag',
    };
    return names[weekday] || `Tag ${weekday}`;
}
/**
 * Formatiert Wochentage als Kurzform
 */
export function formatWeekdaysShort(weekdays) {
    const short = {
        1: 'Mo', 2: 'Di', 3: 'Mi', 4: 'Do', 5: 'Fr', 6: 'Sa', 7: 'So',
    };
    return weekdays.map(d => short[d] || '?').join(', ');
}
/**
 * Formatiert Minuten als "HH:MM" String (mod 1440 für Wrap-Around)
 */
function formatMinutesToTime(minutes) {
    // Wrap around für negative oder > 1440
    const normalized = ((minutes % 1440) + 1440) % 1440;
    const h = Math.floor(normalized / 60);
    const m = normalized % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
}
/**
 * Baut effektive Schichtregeln mit Boundary-Shift (Kulanz-Minuten).
 *
 * ALGORITHMUS:
 * - effectiveStart = originalStart - graceMinutes (Schicht beginnt früher)
 * - effectiveEnd = originalEnd + graceMinutes (Schicht endet später)
 *
 * WICHTIG: Keine Overlap-Prüfung hier - das Design garantiert keine Overlaps,
 * da alle Regeln um den gleichen Wert verschoben werden.
 *
 * @param rules - Original Schichtregeln
 * @param boundaryGraceMinutes - Kulanz-Minuten (0 = keine Verschiebung)
 * @returns Regeln mit angepassten Zeiten für Matching
 */
export function buildEffectiveShiftRules(rules, boundaryGraceMinutes) {
    if (boundaryGraceMinutes <= 0) {
        // Keine Grace-Minuten: Original-Zeiten beibehalten
        return rules.map(r => ({
            ...r,
            effectiveStartTime: r.startTime,
            effectiveEndTime: r.endTime,
            originalStartTime: r.startTime,
            originalEndTime: r.endTime,
        }));
    }
    return rules.map(r => {
        const startMin = parseTimeToMinutes(r.startTime);
        const endMin = parseTimeToMinutes(r.endTime);
        // Verschiebe Grenzen
        const effectiveStartMin = startMin - boundaryGraceMinutes;
        const effectiveEndMin = endMin + boundaryGraceMinutes;
        return {
            ...r,
            // Ersetze für Matching
            startTime: formatMinutesToTime(effectiveStartMin),
            endTime: formatMinutesToTime(effectiveEndMin),
            // Behalte Originale für UI
            effectiveStartTime: formatMinutesToTime(effectiveStartMin),
            effectiveEndTime: formatMinutesToTime(effectiveEndMin),
            originalStartTime: r.startTime,
            originalEndTime: r.endTime,
        };
    });
}
/**
 * Prüft ob ein Timestamp im Grace-Window liegt (vor/nach der Original-Schichtgrenze).
 *
 * Grace-Window-Typen:
 * - 'START': Zeitpunkt liegt vor dem originalen Start, aber nach effectiveStart
 * - 'END': Zeitpunkt liegt nach dem originalen End, aber vor effectiveEnd
 * - null: Zeitpunkt liegt innerhalb der Original-Schichtgrenzen
 *
 * @param rule - Die gematchte Regel (mit effective und original Zeiten)
 * @param timestamp - Der zu prüfende Zeitpunkt
 * @returns 'START' | 'END' | null
 */
export function getGraceWindowType(rule, timestamp) {
    const berlin = toBerlinTime(timestamp);
    const minuteOfDay = berlin.minuteOfDay;
    const originalStartMin = parseTimeToMinutes(rule.originalStartTime);
    const originalEndMin = parseTimeToMinutes(rule.originalEndTime);
    const effectiveStartMin = parseTimeToMinutes(rule.effectiveStartTime);
    const effectiveEndMin = parseTimeToMinutes(rule.effectiveEndTime);
    const isOvernight = originalEndMin < originalStartMin;
    if (!isOvernight) {
        // Normal: z.B. 04:00-14:45
        // Grace-Start: effectiveStart <= minute < originalStart
        if (minuteOfDay >= effectiveStartMin && minuteOfDay < originalStartMin) {
            return 'START';
        }
        // Grace-End: originalEnd <= minute < effectiveEnd
        if (minuteOfDay >= originalEndMin && minuteOfDay < effectiveEndMin) {
            return 'END';
        }
        return null;
    }
    // Overnight: z.B. 14:45-04:00
    // Komplexere Logik wegen Tageswechsel
    // Grace-Start: effectiveStart <= minute < originalStart (am gleichen Tag)
    if (minuteOfDay >= effectiveStartMin && minuteOfDay < originalStartMin) {
        return 'START';
    }
    // Grace-End: originalEnd <= minute < effectiveEnd (am nächsten Tag, d.h. kleine Minuten)
    // Bei overnight ist originalEnd < originalStart, also z.B. 04:00
    if (minuteOfDay >= originalEndMin && minuteOfDay < effectiveEndMin) {
        return 'END';
    }
    return null;
}
/**
 * Prüft ob ein Timestamp im Grace-Window liegt (vereinfacht: ja/nein).
 */
export function isInGraceWindow(rule, timestamp) {
    return getGraceWindowType(rule, timestamp) !== null;
}
//# sourceMappingURL=shift-matcher.js.map